/*
 * Purpose: Drive "watch AI build your flow" — plan, stage blocks onto the map, Take control
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Loader2, Hand } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAutoBlocksStore } from '@/stores/autoBlocksStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import {
  isLiveBuildActive,
  useMissionLiveBuildStore,
} from '@/stores/missionLiveBuildStore';
import {
  LIVE_BUILD_STEP_MS,
  stagedGraphs,
  workflowForStage,
} from '@/lib/missionLiveBuild';
import { preserveWorkflowIdentity } from '@/lib/applyPlanToMap';
import { assembleWorkflowFromPlan } from '@/services/autoblocks/workflowAssembler';
import { APP_ROUTES } from '@/lib/appRoutes';
import type { Workflow } from '@/stores/workflowStore';

/** Query flag set by the home hero / autopilot to request a live map build. */
const BUILD_PARAM = 'building';

export function MissionLiveBuildController() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = useMissionLiveBuildStore((s) => s.status);
  const total = useMissionLiveBuildStore((s) => s.total);
  const inserted = useMissionLiveBuildStore((s) => s.inserted);
  const outcome = useMissionLiveBuildStore((s) => s.outcome);

  const startedRef = useRef(false);
  const cancelledRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fullWorkflowRef = useRef<Workflow | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const takeControl = useCallback(() => {
    cancelledRef.current = true;
    clearTimer();
    const full = fullWorkflowRef.current;
    if (full) {
      useWorkflowStore.getState().applyAssembledWorkflow(full, { persist: true });
      useWorkflowStore.getState().saveWorkflow();
    }
    useMissionLiveBuildStore.getState().finish();
    toast.message('You have the map — edit freely or ask the assistant to change it.');
  }, [clearTimer]);

  useEffect(() => {
    if (searchParams.get(BUILD_PARAM) !== '1' || startedRef.current) return;
    startedRef.current = true;

    // Consume the flag so a refresh does not restart the animation.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(BUILD_PARAM);
    setSearchParams(nextParams, { replace: true });

    const abStore = useMissionLiveBuildStore.getState();
    const auto = useAutoBlocksStore.getState();
    const seedOutcome = auto.outcomeText.trim();

    if (!seedOutcome && !auto.plan) {
      // Nothing to build — leave the map as-is.
      return;
    }

    cancelledRef.current = false;
    abStore.start(seedOutcome || 'your flow');

    const run = async () => {
      let plan = useAutoBlocksStore.getState().plan;

      if (!plan) {
        try {
          await useAutoBlocksStore.getState().directorExpandAndPlan();
        } catch {
          /* handled below via plan check */
        }
        plan = useAutoBlocksStore.getState().plan;
      }

      if (cancelledRef.current) return;

      if (!plan) {
        useMissionLiveBuildStore.getState().reset();
        toast.error('Could not plan that outcome — continue in Compose.', {
          action: { label: 'Open Compose', onClick: () => navigate(APP_ROUTES.compose) },
        });
        navigate(APP_ROUTES.compose, { state: { autoInterpret: true, autopilot: true } });
        return;
      }

      const assembled = preserveWorkflowIdentity(
        assembleWorkflowFromPlan(plan),
        useWorkflowStore.getState().currentWorkflow
      );
      fullWorkflowRef.current = assembled;

      const stages = stagedGraphs(assembled);
      if (stages.length === 0) {
        useWorkflowStore.getState().applyAssembledWorkflow(assembled, { persist: true });
        useWorkflowStore.getState().saveWorkflow();
        useMissionLiveBuildStore.getState().finish();
        return;
      }

      useMissionLiveBuildStore.getState().setBuilding(stages.length);

      const step = (index: number) => {
        if (cancelledRef.current) return;
        const stage = stages[index];
        const partial = workflowForStage(assembled, stage);
        useWorkflowStore.getState().applyAssembledWorkflow(partial, { persist: false, skipUndo: true });
        useMissionLiveBuildStore.getState().setInserted(index + 1);

        if (index + 1 >= stages.length) {
          useWorkflowStore.getState().applyAssembledWorkflow(assembled, { persist: true });
          useWorkflowStore.getState().saveWorkflow();
          useMissionLiveBuildStore.getState().finish();
          return;
        }
        timerRef.current = setTimeout(() => step(index + 1), LIVE_BUILD_STEP_MS);
      };

      step(0);
    };

    void run();
  }, [searchParams, setSearchParams, navigate]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      clearTimer();
    };
  }, [clearTimer]);

  if (!isLiveBuildActive(status)) return null;

  const planning = status === 'planning';
  const label = planning
    ? 'Planning your flow…'
    : `Building your flow — ${inserted}/${total || '…'} blocks`;

  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-30 flex justify-center p-2 sm:p-3">
      <div
        className={cn(
          'pointer-events-auto flex w-full max-w-xl items-center gap-3 rounded-xl border border-primary/30',
          'bg-card/95 px-3 py-2 shadow-lg backdrop-blur-sm sm:px-4 sm:py-2.5'
        )}
        role="status"
        aria-live="polite"
      >
        <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          {planning ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          ) : (
            <Sparkles className="h-4 w-4 text-primary" />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{label}</p>
          {outcome && (
            <p className="truncate text-xs text-muted-foreground">{outcome}</p>
          )}
          {!planning && total > 0 && (
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${Math.round((inserted / total) * 100)}%` }}
              />
            </div>
          )}
        </div>

        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="h-8 shrink-0 gap-1.5 text-xs"
          onClick={takeControl}
        >
          <Hand className="h-3.5 w-3.5" />
          Take control
        </Button>
      </div>
    </div>
  );
}

export default MissionLiveBuildController;
